import { useCommandManager } from '@model-framework/command';
import { PerfProfiler } from '@framework/utils';
import type { GroupId, ModelId, ViewId } from 'balus-common';
import type { UserPublicProfile } from '@user/PublicProfile';
import { useGetTransformedVisibleAreaCenterPoint } from '@user/pages/ViewModelPage/VisibleAreaCenterPointProvider';
import type { StickyModelContentsOperation } from '@view-model/adapter';
import { useTranslation } from 'react-i18next';
import type { WindowEventManager } from '@view-model/application/shortcuts';
import type { StickyModel } from '@view-model/domain/model/StickyModel';
import { ViewSetting } from '@view-model/domain/view';
import type { ViewEntity } from '@view-model/domain/view/ViewEntity';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import type { Point } from '@view-model/models/common/basic';
import { Rect } from '@view-model/models/common/basic/Rect';
import type { CreatingModelComment } from '@view-model/models/sticky/ModelComment';
import { StickyModelContentsView } from '@view-model/ui/components/Model';
import { Fragment, useCallback, useEffect, useState, memo, useRef, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { ConsistencyLinkTargetFrame } from './ConsistencyLinkTargetFrame';
import { useViewLogSender } from './ViewLogSender';
import { ViewPopupMenu } from './ViewPopupMenu';
import { ViewSelectionFrame } from './ViewSelectionFrame';
import { ViewTitleBar } from './ViewTitleBar';
import { MultiSelectionMode } from '@user/pages/ViewModelPage/MultiSelectionMode';
import { useOpenCloseState } from './useOpenCloseState';
import { ViewTitleBarTeleporter } from '@user/pages/ViewModelPage/teleporters';
import { ClientSelectedItemCollectionLoader } from '@view-model/models/sticky/client-selected-items/ClientSelectedItemCollectionLoader';
import { ActionLogEventParamsContextProvider } from '@framework/action-log';
import type { DragContext } from '@model-framework/ui';
import { useHandler } from '@framework/hooks';
import type { GetGuardProps } from '@model-framework/drag-file/useDragFileGuard';
import { EditingDescriptionPanelContextProvider } from '@view-model/models/sticky/DescriptionPanel/hook/EditingDescriptionPanelContext';
import type { DragFileTarget, DragFileTargetActions } from '@model-framework/drag-file/DragFileArea';
import { SVGToImage } from '@model-framework/image/SVGToImage';
import { useViewOperation } from '@view-model/ui/components/View/ViewContext';
import { MeaningPanelTitleBar } from './MeaningPanelTitleBar';
import { ViewCaptureTargetLocator } from './ViewCaptureTargetLocator';
import type { ViewCaptureTarget } from './ViewCaptureTargetLocator';

// NOTE: Propsの型を変更する場合には、propsAreEqual()も変更すること
type Props = {
  readonly: boolean;
  ownerGroupId: GroupId;
  currentUserProfile: UserPublicProfile;
  viewModel: ViewModelEntity;
  viewId: ViewId;
  view: ViewEntity;
  viewPosition: Point;
  model: StickyModel;
  isSelected: boolean;
  onSelectSingleView(viewId: ViewId): void;
  onToggleSelectedViews(viewId: ViewId): void;
  onDeselectAllViews(): void;
  setMultiSelectionMode(multiSelectionMode: MultiSelectionMode): void;
  windowEventManager: WindowEventManager;
  isLinkableTarget: boolean;
  multiSelectionMode: MultiSelectionMode;
  onClickViewModelLink(e: React.MouseEvent): void;
  modelCommentThreadId: string | null;
  stickyModelContentsOperation: StickyModelContentsOperation | undefined;
  creatingComment: CreatingModelComment | undefined;
  onCreatingCommentAdd(modelId: ModelId, position: Point): void;
  onCreatingCommentDrag(modelId: ModelId, dx: number, dy: number): void;
  onCreatingCommentDragEnd(modelId: ModelId): void;
  onCreatingCommentCancel(modelId: ModelId): void;
  onCreatingCommentSubmit(modelId: ModelId, creatingComment: CreatingModelComment): void;
  focusView: (viewId: string) => void;
  openDeleteModal(): void;
  onDragStartView(viewId: ViewId): void;
  onDragView(context: DragContext): void;
  onDragEndView(): void;
  onCopyView(viewId: ViewId): void;
  getViewRectOperations(): Record<ViewId, { view: ViewEntity; rect?: Rect; operation?: StickyModelContentsOperation }>;
  onZoneToView(sourceView: ViewEntity, operation: StickyModelContentsOperation): void;
  getGuardProps: GetGuardProps;
  dispatch: React.Dispatch<DragFileTargetActions>;
  dragFileTargetState: DragFileTarget;
};

const propsAreEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.readonly === nextProps.readonly &&
    prevProps.ownerGroupId === nextProps.ownerGroupId &&
    prevProps.currentUserProfile === nextProps.currentUserProfile &&
    prevProps.viewModel === nextProps.viewModel &&
    prevProps.viewId === nextProps.viewId &&
    prevProps.view.isEqual(nextProps.view) &&
    prevProps.viewPosition.isEqual(nextProps.viewPosition) &&
    prevProps.model.isEqual(nextProps.model) &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.onSelectSingleView === nextProps.onSelectSingleView &&
    prevProps.onToggleSelectedViews === nextProps.onToggleSelectedViews &&
    prevProps.onDeselectAllViews === nextProps.onDeselectAllViews &&
    prevProps.setMultiSelectionMode === nextProps.setMultiSelectionMode &&
    prevProps.windowEventManager === nextProps.windowEventManager &&
    prevProps.isLinkableTarget === nextProps.isLinkableTarget &&
    prevProps.multiSelectionMode === nextProps.multiSelectionMode &&
    prevProps.onClickViewModelLink === nextProps.onClickViewModelLink &&
    prevProps.modelCommentThreadId === nextProps.modelCommentThreadId &&
    prevProps.stickyModelContentsOperation === nextProps.stickyModelContentsOperation &&
    prevProps.creatingComment === nextProps.creatingComment &&
    prevProps.onCreatingCommentAdd === nextProps.onCreatingCommentAdd &&
    prevProps.onCreatingCommentDrag === nextProps.onCreatingCommentDrag &&
    prevProps.onCreatingCommentDragEnd === nextProps.onCreatingCommentDragEnd &&
    prevProps.onCreatingCommentCancel === nextProps.onCreatingCommentCancel &&
    prevProps.onCreatingCommentSubmit === nextProps.onCreatingCommentSubmit &&
    prevProps.focusView === nextProps.focusView &&
    prevProps.openDeleteModal === nextProps.openDeleteModal &&
    prevProps.onDragStartView === nextProps.onDragStartView &&
    prevProps.onDragView === nextProps.onDragView &&
    prevProps.onDragEndView === nextProps.onDragEndView &&
    prevProps.onCopyView === nextProps.onCopyView &&
    prevProps.getViewRectOperations === nextProps.getViewRectOperations &&
    prevProps.onZoneToView === nextProps.onZoneToView &&
    prevProps.getGuardProps === nextProps.getGuardProps &&
    prevProps.dispatch === nextProps.dispatch &&
    prevProps.dragFileTargetState === nextProps.dragFileTargetState
  );
};

type SwapperProps = {
  swap: boolean;
  a: React.ReactNode;
  b: React.ReactNode;
};

const Swapper: React.FC<SwapperProps> = ({ swap, a, b }: SwapperProps) => {
  // Fragmentにkeyを指定し再レンダリングさせないようにしている
  const compA = <Fragment key="a">{a}</Fragment>;
  const compB = <Fragment key="b">{b}</Fragment>;

  return <>{swap ? [compB, compA] : [compA, compB]}</>;
};

const ViewFn: React.FC<Props> = ({
  readonly,
  ownerGroupId,
  currentUserProfile,
  viewModel,
  view,
  viewPosition,
  model,
  isSelected,
  onToggleSelectedViews,
  onSelectSingleView,
  onDeselectAllViews,
  setMultiSelectionMode,
  windowEventManager,
  isLinkableTarget,
  multiSelectionMode,
  onClickViewModelLink,
  modelCommentThreadId,
  stickyModelContentsOperation,
  creatingComment,
  onCreatingCommentAdd,
  onCreatingCommentDrag,
  onCreatingCommentDragEnd,
  onCreatingCommentCancel,
  onCreatingCommentSubmit,
  focusView,
  openDeleteModal,
  onDragStartView,
  onDragView,
  onDragEndView,
  onCopyView,
  getViewRectOperations,
  onZoneToView,
  getGuardProps,
  dispatch,
  dragFileTargetState,
}: Props) => {
  const { t } = useTranslation();
  const logSender = useViewLogSender(view);
  const commandManager = useCommandManager();
  const viewOperation = useViewOperation();

  const [isPopupMenuOpen, openPopupMenu, closePopupMenu] = useOpenCloseState();
  const [isResizing, setIsResizing] = useState(false);

  const getTransformedVisibleAreaCenterPoint = useGetTransformedVisibleAreaCenterPoint();

  const viewRect = useMemo(() => new Rect(viewPosition, view.size), [view.size, viewPosition]);

  const handleToggleSelectedViews = useCallback(() => {
    onToggleSelectedViews(view.id);
  }, [onToggleSelectedViews, view.id]);

  const handleSelectSingleView = useHandler((viewId?: ViewId) => {
    onSelectSingleView(viewId ?? view.id);
  });

  const handleDeleteView = useCallback(() => {
    if (view.viewType !== 'meaningPanel') openDeleteModal();
  }, [openDeleteModal, view.viewType]);

  const handleClickView = useCallback(
    (e: MouseEvent) => {
      if (!stickyModelContentsOperation) return;

      if (isSelected) {
        if (e.shiftKey || e.ctrlKey || e.metaKey) {
          if (multiSelectionMode.isMultiElementsSelectionMode) {
            handleSelectSingleView();
            setMultiSelectionMode(MultiSelectionMode.offMode);
          } else {
            handleToggleSelectedViews();
          }
        } else {
          stickyModelContentsOperation.deselectAll();
          handleSelectSingleView();
          setMultiSelectionMode(MultiSelectionMode.offMode);
        }
      } else {
        if (e.shiftKey || e.ctrlKey || e.metaKey) {
          if (multiSelectionMode.isMultiElementsSelectionMode) {
            stickyModelContentsOperation.deselectAll();
            handleSelectSingleView();
            setMultiSelectionMode(MultiSelectionMode.offMode);
          } else if (multiSelectionMode.isMultiViewsSelectionMode) {
            handleToggleSelectedViews();
          } else if (multiSelectionMode.isReadyForMultiSelection) {
            handleToggleSelectedViews();
            setMultiSelectionMode(MultiSelectionMode.multiViewsSelectionMode);
          } else {
            onDeselectAllViews();
            handleToggleSelectedViews();
            setMultiSelectionMode(MultiSelectionMode.multiViewsSelectionMode);
          }
        } else {
          handleSelectSingleView();
          // すでに選択されたビューがある状態の時は、その後複数選択キーを押しながら他のビューを複数選択できる必要があるため、ここでは複数選択準備モードに移行する
          setMultiSelectionMode(MultiSelectionMode.readyForMultiSelectionMode);
        }
      }
    },
    [
      handleSelectSingleView,
      handleToggleSelectedViews,
      multiSelectionMode,
      stickyModelContentsOperation,
      setMultiSelectionMode,
      isSelected,
      onDeselectAllViews,
    ]
  );

  // 修飾キー（Shift/Ctrl/Meta）押下時はタイトルバーのドラッグ（ビュー移動）を抑制し、
  // ビューの複数選択トグルとして扱う
  const shouldSuppressTitleBarDrag = useCallback((e: MouseEvent) => e.shiftKey || e.ctrlKey || e.metaKey, []);

  const handleResizeStart = useCallback(() => setIsResizing(true), []);
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  const handleCopyView = useCallback(() => {
    onCopyView(view.id);
  }, [onCopyView, view.id]);

  const viewSVGGRef = useRef<SVGGElement>(null);
  const viewTitleSVGGRef = useRef<SVGGElement>(null);

  // MCPブリッジ経由のビュー単体キャプチャで viewId からキャプチャ対象の G 要素を
  // 解決できるよう、マウント中のみレジストリへ登録する
  useEffect(() => {
    const target: ViewCaptureTarget = {
      getView: () => viewSVGGRef.current,
      getViewTitle: () => viewTitleSVGGRef.current,
    };
    ViewCaptureTargetLocator.register(view.id, target);
    return () => ViewCaptureTargetLocator.unregister(view.id, target);
  }, [view.id]);

  const handleExportViewAsImage = useCallback(async () => {
    closePopupMenu();

    if (!viewSVGGRef.current || !viewTitleSVGGRef.current) {
      // eslint-disable-next-line no-console
      console.log('[handleExportViewAsImage] viewSVGGRef or viewTitleSVGGRef is null');
      return;
    }

    toast(t('common.message.imageExportStarted'));

    if (
      await SVGToImage.exportFromViewParts(
        viewSVGGRef.current,
        viewTitleSVGGRef.current,
        viewRect,
        `${view.name.toReadableName()}.png`
      )
    ) {
      toast.success(t('common.message.imageExportCompleted'));
    } else {
      toast.error(t('common.message.imageExportFailed'));
    }
  }, [closePopupMenu, t, view.name, viewRect]);

  const handleCreatingCommentAdd = useCallback(() => {
    const transformedVisibleAreaCenterPoint = getTransformedVisibleAreaCenterPoint(view.getRect(viewPosition));
    onCreatingCommentAdd(model.id, transformedVisibleAreaCenterPoint);
  }, [model.id, onCreatingCommentAdd, getTransformedVisibleAreaCenterPoint, view, viewPosition]);

  const handleCreatingCommentDrag = useCallback(
    (dx: number, dy: number) => {
      onCreatingCommentDrag(model.id, dx, dy);
    },
    [model.id, onCreatingCommentDrag]
  );

  const handleCreatingCommentDragEnd = useCallback(
    () => onCreatingCommentDragEnd(model.id),
    [model.id, onCreatingCommentDragEnd]
  );

  const handleCreatingCommentSubmit = useCallback(
    (creatingModelComment: CreatingModelComment) => onCreatingCommentSubmit(model.id, creatingModelComment),
    [model.id, onCreatingCommentSubmit]
  );

  const handleCreatingCommentCancel = useCallback(
    () => onCreatingCommentCancel(model.id),
    [model.id, onCreatingCommentCancel]
  );

  const handleFocusView = useCallback(() => focusView(view.id), [focusView, view.id]);

  const handleAddMeaningPanelView = useCallback(async () => {
    await viewOperation.addMeaningPanelView();
    toast(t('canvas.view.toast.meaningPanelAdded'));
  }, [t, viewOperation]);

  const handleToggleMeaningPanel = useHandler(async () => {
    if (!view.meaningPanelViewId) return;
    await viewOperation.changeMeaningPanelOpenState(view.meaningPanelViewId);
  });

  const handleToggleShowNodeCreatedUser = useHandler(async () => {
    const setting = new ViewSetting(
      !view.setting.showNodeCreatedUser,
      view.setting.isLocked,
      view.setting.isPositionFixed
    );
    await viewOperation.handleSettingChanged(setting);
  });

  const handleCloseMeaningPanel = useHandler(async () => {
    await viewOperation.changeMeaningPanelOpenState(view.id, false);
  });

  return (
    <>
      {isLinkableTarget && <ConsistencyLinkTargetFrame viewRect={viewRect} />}
      {/* リサイズ中にviewContentをresizeViewの背面に持ってくるようにしている。 */}
      <Swapper
        swap={isResizing}
        a={
          <>
            {isSelected && (
              <ViewSelectionFrame
                readonly={readonly || view.setting.isLocked}
                isMeaningPanel={view.viewType === 'meaningPanel'}
                viewRect={viewRect}
                onResizeStart={handleResizeStart}
                onResizeEnd={handleResizeEnd}
                getGuardProps={getGuardProps}
              />
            )}
          </>
        }
        b={
          <g data-view-id={view.id} transform={viewRect.topLeft().toSVGTranslate()} ref={viewSVGGRef}>
            {/* モデルバージョンの変更によって、必ずコンポーネントが再レンダリングされるように、 key にバージョンを含める */}
            {stickyModelContentsOperation && (
              <ActionLogEventParamsContextProvider modelId={model.id} modelType={model.type}>
                <EditingDescriptionPanelContextProvider>
                  <PerfProfiler id={`view:${view.id}:model`}>
                    <StickyModelContentsView
                      key={`${model.key.toString()}-${model.version}`}
                      readonly={readonly || view.setting.isLocked}
                      ownerGroupId={ownerGroupId}
                      currentUserProfile={currentUserProfile}
                      viewModel={viewModel}
                      view={view}
                      model={model}
                      isSelected={isSelected}
                      getViewRectOperations={getViewRectOperations}
                      onSelectSingleView={handleSelectSingleView}
                      onToggleSelectedViews={handleToggleSelectedViews}
                      onDeselectAllViews={onDeselectAllViews}
                      setMultiSelectionMode={setMultiSelectionMode}
                      onDeleteView={handleDeleteView}
                      onClickViewModelLink={onClickViewModelLink}
                      showNodeCreatedUser={view.setting.showNodeCreatedUser}
                      windowEventManager={windowEventManager}
                      multiSelectionMode={multiSelectionMode}
                      commandManager={commandManager}
                      canvasSize={viewRect.size}
                      logSender={logSender}
                      modelCommentThreadId={modelCommentThreadId}
                      operation={stickyModelContentsOperation}
                      creatingComment={creatingComment}
                      onCreatingCommentAdd={handleCreatingCommentAdd}
                      onCreatingCommentDrag={handleCreatingCommentDrag}
                      onCreatingCommentDragEnd={handleCreatingCommentDragEnd}
                      onCreatingCommentCancel={handleCreatingCommentCancel}
                      onCreatingCommentSubmit={handleCreatingCommentSubmit}
                      onCreatingCommentAddByRightClick={onCreatingCommentAdd}
                      onZoneToView={onZoneToView}
                      onCopyView={handleCopyView}
                      getTransformedVisibleAreaCenterPoint={getTransformedVisibleAreaCenterPoint}
                      viewRect={viewRect}
                      getGuardProps={getGuardProps}
                      dispatch={dispatch}
                      dragFileTargetState={dragFileTargetState}
                      onClickView={handleClickView}
                    />
                  </PerfProfiler>
                </EditingDescriptionPanelContextProvider>
              </ActionLogEventParamsContextProvider>
            )}
          </g>
        }
      />

      <ViewTitleBarTeleporter.Source>
        <g ref={viewTitleSVGGRef}>
          {view.viewType === 'meaningPanel' ? (
            <MeaningPanelTitleBar
              viewRect={viewRect}
              width={viewRect.width}
              readonly={readonly}
              onClickCloseButton={handleCloseMeaningPanel}
            />
          ) : (
            <>
              <ViewTitleBar
                viewModelId={viewModel.id}
                readonly={readonly}
                hasMeaningPanel={!!view.meaningPanelViewId}
                viewIsLocked={view.setting.isLocked}
                viewIsPositionFixed={view.setting.isPositionFixed}
                viewRect={viewRect}
                name={view.name}
                viewId={view.id}
                onClick={(e) => handleClickView(e.nativeEvent)}
                onCopyView={handleCopyView}
                onOpenPopupMenu={() => {
                  openPopupMenu();
                  handleSelectSingleView();
                }}
                onDragStart={onDragStartView}
                onDrag={onDragView}
                onDragEnd={onDragEndView}
                onAddMeaningPanel={handleAddMeaningPanelView}
                onToggleMeaningPanel={handleToggleMeaningPanel}
                onToggleShowNodeCreatedUser={handleToggleShowNodeCreatedUser}
                viewThemeColor={view.themeColor}
                getGuardProps={getGuardProps}
                shouldSuppressDrag={shouldSuppressTitleBarDrag}
              />
              <ViewPopupMenu
                isOpen={isPopupMenuOpen}
                viewRect={viewRect}
                viewModelId={viewModel.id}
                viewId={view.id}
                viewSetting={view.setting}
                onDelete={openDeleteModal}
                onFocusView={handleFocusView}
                onClose={closePopupMenu}
                onExportViewAsImage={handleExportViewAsImage}
                viewThemeColor={view.themeColor}
              />
            </>
          )}
        </g>
      </ViewTitleBarTeleporter.Source>
      <ClientSelectedItemCollectionLoader viewModelId={viewModel.id} modelId={model.id} />
    </>
  );
};

export const View = memo(ViewFn, propsAreEqual);
